import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, Heart, Users, Globe, BookOpen, ShoppingBag, Image,
  LogIn, UserPlus, Sparkles, MapPin, Mail, ShieldCheck, Code2, Scale
} from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import PadaidLogo from '../components/ui/PadaidLogo';
import { getPage } from '../services/pages';
import API_BASE_URL from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const BENTO_IMG = {
  hero: 'https://images.pexels.com/photos/6646899/pexels-photo-6646899.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  don: 'https://images.pexels.com/photos/7345447/pexels-photo-7345447.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  galerie: 'https://images.pexels.com/photos/6646882/pexels-photo-6646882.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
};

// Icônes disponibles pour les blocs "actions" gérés depuis le Page Builder
const ICONS = {
  heart: Heart,
  users: Users,
  globe: Globe,
  book: BookOpen,
  shop: ShoppingBag,
  image: Image,
};



// Contenu par défaut affiché tant que le fondateur n'a rien personnalisé
// depuis Admin > Page Builder (slug "home"). Dès qu'une page CMS existe,
// ce contenu est entièrement remplacé, section par section.
const DEFAULT_BLOCKS = [
  {
    id: 'default-actions',
    type: 'actions',
    data: {
      section_title: 'Nos Actions',
      section_subtitle: "Découvrez comment PADAID agit concrètement pour le développement international et l'aide humanitaire.",
      items: [
        { icon: 'heart', title: 'Aide Humanitaire', desc: "Interventions d'urgence et aide aux populations en détresse dans le monde entier." },
        { icon: 'users', title: 'Développement', desc: "Projets de développement durable et programmes d'autonomisation des communautés locales." },
        { icon: 'globe', title: 'Sensibilisation', desc: "Campagnes de sensibilisation et éducation sur les enjeux du développement international." },
      ],
    },
  },
  {
    id: 'default-stats',
    type: 'stats',
    data: {
      stats: [
        { value: '169', label: 'Membres opérants' },
        { value: '13', label: 'Pays d\'intervention' },
        { value: '3', label: 'Ans d\'action' },
      ],
    },
  },
  {
    id: 'default-trust',
    type: 'trust',
    data: {
      section_title: 'Pourquoi nous faire confiance',
      items: [
        { icon: 'shield', title: 'Double authentification', desc: "Comptes admin protégés par 2FA et vérification d'appareil." },
        { icon: 'code', title: 'Contenu géré sans code', desc: 'Textes, images et pages mis à jour depuis un panneau d\'administration.' },
        { icon: 'scale', title: 'Veille légale', desc: 'Nouveautés réglementaires détectées automatiquement, jamais appliquées sans validation humaine.' },
        { icon: 'map', title: 'Sources citées', desc: 'Chaque adresse tierce (Watizat, Lou Pitchoun Solidaire) reste attribuée et datée.' },
      ],
    },
  },
  {
    id: 'default-cta',
    type: 'cta',
    data: {
      title: 'Rejoignez notre mission',
      text: 'Que ce soit par votre adhésion, vos dons ou votre engagement, chaque geste compte pour construire un monde plus solidaire.',
      btn1_text: 'Devenir membre',
      btn1_link: '/membership',
      btn2_text: 'Nous contacter',
      btn2_link: '/contact',
    },
  },
];

// ---------------------------------------------------------------------
// Hook: reveal-on-scroll générique (utilisé par tous les blocs CMS)
// ---------------------------------------------------------------------
const useReveal = () => {
  const nodesRef = useRef(new Map());
  const [visible, setVisible] = useState(() => new Set());

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const key = entry.target.getAttribute('data-reveal-key');
          setVisible((prev) => {
            if (prev.has(key)) return prev;
            const next = new Set(prev);
            next.add(key);
            return next;
          });
        }
      });
    }, { threshold: 0.15 });

    nodesRef.current.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  const register = (key) => (node) => {
    if (!node) return;
    node.setAttribute('data-reveal-key', key);
    nodesRef.current.set(key, node);
  };

  const isVisible = (key) => visible.has(key);

  return { register, isVisible };
};

// ---------------------------------------------------------------------
// Renderers de blocs CMS (miroir exact des types édités dans le Page Builder)
// ---------------------------------------------------------------------
const ActionsBlock = ({ block, reveal }) => {
  const items = (block.data.items && block.data.items.length ? block.data.items : DEFAULT_BLOCKS[0].data.items);
  return (
    <section className="relative py-20 bg-card/50 overflow-hidden">
      <div className="grid-overlay" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-secondary mb-4">
            <span className="pulse-dot" /> En mouvement
          </span>
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            {block.data.section_title || 'Nos Actions'}
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {block.data.section_subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {items.map((item, idx) => {
            const Icon = ICONS[item.icon] || Heart;
            const key = `actions-${idx}`;
            return (
              <Card
                key={idx}
                ref={reveal.register(key)}
                className={`glow-border tilt-card group border-border/50 rounded-2xl bg-background/60 backdrop-blur-sm transition-all duration-500 hover:-translate-y-1 ${reveal.isVisible(key) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/15 to-secondary/15 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const StatsBlock = ({ block, reveal }) => {
  const stats = block.data.stats || [];
  if (!stats.length) return null;
  const gridClass = stats.length === 3
    ? 'grid grid-cols-1 sm:grid-cols-3 gap-6'
    : 'grid grid-cols-2 md:grid-cols-4 gap-6';
  return (
    <section className="relative py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={gridClass}>
          {stats.map((s, idx) => {
            const key = `stats-${idx}`;
            return (
              <div
                key={idx}
                ref={reveal.register(key)}
                className={`glass-panel rounded-2xl p-6 text-center transition-all duration-500 ${reveal.isVisible(key) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              >
                <div className="text-3xl lg:text-4xl font-bold text-gradient-animated">{s.value}</div>
                <div className="text-sm text-muted-foreground mt-1">{s.label}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const TextBlock = ({ block, reveal }) => {
  const key = 'text-block';
  return (
    <section className="relative py-16">
      <div
        ref={reveal.register(key)}
        className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-500 ${reveal.isVisible(key) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      >
        <p className="text-lg text-muted-foreground whitespace-pre-wrap">{block.data.text}</p>
      </div>
    </section>
  );
};

const GalleryTeaserBlock = ({ block, reveal }) => {
  const key = 'gallery-teaser';
  return (
    <section className="relative py-16 bg-card/40">
      <div
        ref={reveal.register(key)}
        className={`max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-500 ${reveal.isVisible(key) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      >
        <h3 className="text-2xl font-bold mb-3">{block.data.title || 'Galerie'}</h3>
        <Button asChild size="lg" className="bg-primary hover:bg-primary/90">
          <Link to={block.data.link || '/gallery'}>{block.data.btn || 'Voir la galerie'} <ArrowRight className="ml-2 h-4 w-4" /></Link>
        </Button>
      </div>
    </section>
  );
};

const CtaBlock = ({ block, reveal }) => {
  const key = 'cta-block';
  return (
    <section
      ref={reveal.register(key)}
      className={`relative py-20 overflow-hidden transition-all duration-500 ${reveal.isVisible(key) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
    >
      <div className="mesh-bg" />
      <div className="relative max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl lg:text-4xl font-bold mb-6">
          {block.data.title || 'Rejoignez notre mission'}
        </h2>
        <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
          {block.data.text}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {block.data.btn1_text && (
            <Button asChild size="lg" className="bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20">
              <Link to={block.data.btn1_link || '/membership'}>{block.data.btn1_text}</Link>
            </Button>
          )}
          {block.data.btn2_text && (
            <Button asChild variant="outline" size="lg" className="border-secondary text-secondary hover:bg-secondary hover:text-background">
              <Link to={block.data.btn2_link || '/contact'}>{block.data.btn2_text}</Link>
            </Button>
          )}
        </div>
      </div>
    </section>
  );
};

// Icônes disponibles pour le bloc "confiance"
const TRUST_ICONS = {
  shield: ShieldCheck,
  code: Code2,
  scale: Scale,
  map: MapPin,
};

const TrustBlock = ({ block, reveal }) => {
  const { t } = useLanguage();
  const defaults = [
    { icon: 'shield', title: t('trust.t1_title'), desc: t('trust.t1_desc') },
    { icon: 'code', title: t('trust.t2_title'), desc: t('trust.t2_desc') },
    { icon: 'scale', title: t('trust.t3_title'), desc: t('trust.t3_desc') },
    { icon: 'map', title: t('trust.t4_title'), desc: t('trust.t4_desc') },
  ];
  const items = (block.data.items && block.data.items.length ? block.data.items : defaults);
  const sectionTitle = block.data.section_title || t('trust.title');
  return (
    <section className="relative py-16" data-testid="home-trust">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {sectionTitle && (
          <h2 className="text-2xl lg:text-3xl font-bold text-center mb-10">{sectionTitle}</h2>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map((item, idx) => {
            const Icon = TRUST_ICONS[item.icon] || ShieldCheck;
            const key = `trust-${idx}`;
            return (
              <div
                key={idx}
                ref={reveal.register(key)}
                className={`glass-panel rounded-2xl p-6 text-center transition-all duration-500 ${reveal.isVisible(key) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              >
                <Icon className="h-7 w-7 text-primary mx-auto mb-3" />
                <h4 className="font-semibold text-sm mb-1.5">{item.title}</h4>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const BLOCK_RENDERERS = {
  actions: ActionsBlock,
  stats: StatsBlock,
  text: TextBlock,
  gallery: GalleryTeaserBlock,
  cta: CtaBlock,
  trust: TrustBlock,
};

// ---------------------------------------------------------------------
// Hero — reste géré dans Home.jsx (parallax + orbe), personnalisable
// via un bloc "hero" du Page Builder si présent, sinon valeurs par défaut.
// ---------------------------------------------------------------------
const Hero = ({ heroData }) => {
  const { t } = useLanguage();
  const heroRef = useRef(null);
  const titleRef = useRef(null);
  const orbRef = useRef(null);
  const actionsRef = useRef(null);
  const [isDesktop, setIsDesktop] = useState(true);

  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth >= 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    const bg = heroRef.current;
    const title = titleRef.current;
    if (!bg || !title) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      window.requestAnimationFrame(() => {
        const y = window.scrollY || 0;
        bg.style.transform = `translateY(${Math.min(50, y * 0.15)}px)`;
        title.style.transform = `translateY(${Math.min(24, y * 0.08)}px)`;
        ticking = false;
      });
      ticking = true;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const orb = orbRef.current;
    if (!orb) return;
    const move = (e) => {
      if (!isDesktop) return;
      const { innerWidth, innerHeight } = window;
      const x = (e.clientX / innerWidth - 0.5) * 24;
      const y = (e.clientY / innerHeight - 0.5) * 24;
      orb.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px)`;
    };
    window.addEventListener('mousemove', move);
    return () => window.removeEventListener('mousemove', move);
  }, [isDesktop]);

  const title = heroData?.title || t('hero.title');
  const subtitle1 = heroData?.subtitle || t('hero.subtitle1');
  const subtitle2 = heroData?.text2 || t('hero.subtitle2');
  const ctaText = heroData?.cta_text || t('hero.cta');
  const ctaLink = heroData?.cta_link || '/membership';

  return (
    <section className="relative py-20 lg:py-32 overflow-hidden">
      <div ref={heroRef} className="absolute inset-0 bg-gradient-to-br from-background/50 via-background/25 to-transparent will-change-transform" />
      <div className="mesh-bg" />
      <div className="grid-overlay" />
      <div className="absolute -top-10 -right-10 w-72 h-72 bg-primary/10 rounded-full blur-2xl float-orb" ref={orbRef} />
      <div className="absolute bottom-0 left-0 w-56 h-56 bg-secondary/10 rounded-full blur-2xl float-orb" style={{ animationDelay: '2s' }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-end gap-4 mb-8">
          <Button variant="outline" size="sm" className="border-primary text-primary hover:bg-primary hover:text-white" asChild>
            <Link to="/login"><LogIn className="mr-2 h-4 w-4" />{t('hero.login')}</Link>
          </Button>
          <Button size="sm" className="bg-secondary hover:bg-secondary/90 text-black" asChild>
            <Link to="/register"><UserPlus className="mr-2 h-4 w-4" />{t('hero.register')}</Link>
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex items-center justify-center lg:justify-start mb-6">
              <PadaidLogo size="xl" showText={false} />
            </div>
            <h1 ref={titleRef} className="text-4xl lg:text-6xl font-bold mb-6 will-change-transform">
              <div className="padaid-logo text-4xl lg:text-6xl">
                <span className="pa-red">PA</span>
                <span className="daid-green">DAID</span>
              </div>
              <span className="block text-2xl lg:text-3xl mt-2 text-gradient-animated">
                {title}
              </span>
            </h1>
            <p className="text-lg text-muted-foreground mb-4 max-w-lg">{subtitle1}</p>
            <p className="text-base text-muted-foreground mb-8 max-w-lg">{subtitle2}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20">
                <Link to={ctaLink}>{ctaText} <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-primary text-primary hover:bg-primary/10"
                onClick={() => { try { actionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {} }}
              >
                {t('hero.discover')}
              </Button>
              <Button asChild variant="outline" size="lg" className="border-secondary text-secondary hover:bg-secondary hover:text-background">
                <Link to="/donate">{t('hero.donate')} <Heart className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
          </div>
          <div className="relative">
            <div className="glow-border group aspect-square rounded-3xl overflow-hidden backdrop-blur-sm border border-border shadow-2xl shadow-primary/10">
              <img
                src="https://images.unsplash.com/photo-1710093072228-8c3129f27357?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
                alt="Bénévoles PADAID en action"
                loading="eager"
                data-testid="hero-image"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#4A0012]/85 via-[#4A0012]/25 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 z-10 p-6">
                <p className="text-white text-base sm:text-lg font-semibold drop-shadow-lg">{subtitle1}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div ref={actionsRef} className="absolute bottom-0" />
    </section>
  );
};

// ---------------------------------------------------------------------
// Bento grid premium (style Apple + glassmorphism) — vitrine des espaces clés
// ---------------------------------------------------------------------
const GLASS = 'bg-white/70 dark:bg-[#0A0004]/70 backdrop-blur-2xl border border-white/50 dark:border-white/10 shadow-[0_8px_30px_rgba(0,0,0,0.06)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.5)]';

const BentoGrid = () => {
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/site/stats`);
        if (!cancelled) setStats(res.data);
      } catch (e) { /* garde les tirets */ }
    })();
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/site/bento`);
        if (!cancelled) setCfg(res.data);
      } catch (e) { /* defaults */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const c = cfg || {};
  const heroImg = c.hero_image || BENTO_IMG.hero;
  const galerieImg = c.galerie_image || BENTO_IMG.galerie;

  const cellBase = 'group relative overflow-hidden rounded-[2rem] transition-all duration-500 hover:scale-[1.015] hover:shadow-xl flex flex-col animate-in fade-in slide-in-from-bottom-4 fill-mode-both';

  return (
    <section className="relative py-20" data-testid="home-bento">
      <div className="mesh-bg" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10 max-w-2xl">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-secondary mb-3">
            <Sparkles className="h-4 w-4" /> {c.eyebrow || t('bento.eyebrow')}
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-br from-black to-rose-900 dark:from-rose-200 dark:to-rose-500">
            {c.title || t('bento.title')}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 auto-rows-[190px] gap-4 md:gap-5 grid-flow-row-dense">
          {/* Hero mission */}
          <Link to="/membership" data-testid="bento-mission" className={`${cellBase} md:col-span-2 md:row-span-2 text-white`} style={{ animationDelay: '0ms' }}>
            <img src={heroImg} alt="Bénévoles PADAID" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#4A0012]/90 via-[#4A0012]/50 to-black/30" />
            <div className="relative z-10 mt-auto p-7">
              <h3 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">{c.mission_title || t('bento.mission_title')}</h3>
              <p className="text-white/85 text-sm sm:text-base max-w-md mb-5">{c.mission_desc || t('bento.mission_desc')}</p>
              <div className="flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold shadow-lg shadow-emerald-500/30 transition-transform active:scale-95 group-hover:gap-3">{t('bento.discover')} <ArrowRight className="h-4 w-4" /></span>
                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-md border border-white/30 px-5 py-2.5 text-sm font-semibold">{t('bento.join_us')}</span>
              </div>
            </div>
          </Link>

          {/* Faire un don (emerald) */}
          <Link to="/donate" data-testid="bento-don" className={`${cellBase} bg-gradient-to-br from-emerald-500 to-emerald-600 text-white`} style={{ animationDelay: '60ms' }}>
            <div className="relative z-10 flex h-full flex-col justify-between p-6">
              <div className="flex items-center justify-between">
                <Heart className="h-8 w-8" />
                <ArrowUpRight className="h-5 w-5 opacity-70 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </div>
              <div>
                <h3 className="text-xl font-bold">{c.don_title || t('bento.don_title')}</h3>
                <p className="text-white/85 text-sm">{c.don_desc || t('bento.don_desc')}</p>
              </div>
            </div>
          </Link>

          {/* Impact stats (live) */}
          <div data-testid="bento-stats" className={`${cellBase} ${GLASS}`} style={{ animationDelay: '120ms' }}>
            <div className="relative z-10 flex h-full flex-col justify-center gap-3 p-5">
              <div className="grid grid-cols-3 items-center gap-1 text-center">
                {[
                  { v: stats?.active_members, l: t('bento.members') },
                  { v: stats?.intervention_countries, l: t('bento.countries') },
                  { v: stats?.years_experience, l: t('bento.years') },
                ].map((s, i) => (
                  <div key={i}>
                    <div className="text-3xl font-bold text-gradient-animated">{s.v != null ? s.v : '—'}</div>
                    <div className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Galerie (tall) */}
          <Link to="/gallery" data-testid="bento-galerie" className={`${cellBase} md:row-span-2 text-white`} style={{ animationDelay: '180ms' }}>
            <img src={galerieImg} alt="Missions PADAID" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            <div className="relative z-10 mt-auto p-6">
              <Image className="h-7 w-7 mb-2" />
              <h3 className="text-xl font-bold">{c.galerie_title || t('bento.galerie_title')}</h3>
              <p className="text-white/85 text-sm">{c.galerie_desc || t('bento.galerie_desc')}</p>
            </div>
          </Link>

          {/* Carte des zones */}
          <Link to="/carte" data-testid="bento-carte" className={`${cellBase} md:col-span-2 ${GLASS}`} style={{ animationDelay: '240ms' }}>
            <div className="relative z-10 flex h-full items-center justify-between gap-4 p-6">
              <div>
                <MapPin className="h-7 w-7 text-primary mb-2" />
                <h3 className="text-xl font-bold">{c.carte_title || t('bento.carte_title')}</h3>
                <p className="text-muted-foreground text-sm">{c.carte_desc || t('bento.carte_desc')}</p>
              </div>
              <ArrowUpRight className="h-6 w-6 text-primary transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </div>
          </Link>

          {/* Boutique */}
          <Link to="/shop" data-testid="bento-boutique" className={`${cellBase} ${GLASS}`} style={{ animationDelay: '300ms' }}>
            <div className="relative z-10 flex h-full flex-col justify-between p-6">
              <ShoppingBag className="h-8 w-8 text-primary" />
              <div>
                <h3 className="text-xl font-bold">{c.boutique_title || t('bento.boutique_title')}</h3>
                <p className="text-muted-foreground text-sm">{c.boutique_desc || t('bento.boutique_desc')}</p>
              </div>
            </div>
          </Link>

          {/* Blog / Tracts */}
          <Link to="/blog" data-testid="bento-blog" className={`${cellBase} ${GLASS}`} style={{ animationDelay: '360ms' }}>
            <div className="relative z-10 flex h-full flex-col justify-between p-6">
              <BookOpen className="h-8 w-8 text-primary" />
              <div>
                <h3 className="text-xl font-bold">{c.blog_title || t('bento.blog_title')}</h3>
                <p className="text-muted-foreground text-sm">{c.blog_desc || t('bento.blog_desc')}</p>
              </div>
            </div>
          </Link>

          {/* Équipe */}
          <Link to="/founders" data-testid="bento-equipe" className={`${cellBase} ${GLASS}`} style={{ animationDelay: '420ms' }}>
            <div className="relative z-10 flex h-full flex-col justify-between p-6">
              <Users className="h-8 w-8 text-primary" />
              <div>
                <h3 className="text-xl font-bold">{c.equipe_title || t('bento.equipe_title')}</h3>
                <p className="text-muted-foreground text-sm">{c.equipe_desc || t('bento.equipe_desc')}</p>
              </div>
            </div>
          </Link>

          {/* Contact */}
          <Link to="/contact" data-testid="bento-contact" className={`${cellBase} bg-gradient-to-br from-[#800020] to-[#4A0012] text-white`} style={{ animationDelay: '480ms' }}>
            <div className="relative z-10 flex h-full flex-col justify-between p-6">
              <Mail className="h-8 w-8" />
              <div>
                <h3 className="text-xl font-bold">{c.contact_title || t('bento.contact_title')}</h3>
                <p className="text-white/85 text-sm">{c.contact_desc || t('bento.contact_desc')}</p>
              </div>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  const [blocks, setBlocks] = useState(null); // null = chargement, [] = page vide en CMS
  const reveal = useReveal();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getPage('home');
        const page = res?.page;
        if (!cancelled) {
          setBlocks(page && page.blocks && page.blocks.length ? page.blocks : DEFAULT_BLOCKS);
        }
      } catch (e) {
        if (!cancelled) setBlocks(DEFAULT_BLOCKS);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const heroBlock = useMemo(() => (blocks || []).find((b) => b.type === 'hero'), [blocks]);
  const otherBlocks = useMemo(() => (blocks || []).filter((b) => b.type !== 'hero'), [blocks]);

  return (
    <div className="relative min-h-screen">
      <div className="relative z-10">
        <Hero heroData={heroBlock?.data} />
        {otherBlocks.map((block) => {
          const Renderer = BLOCK_RENDERERS[block.type];
          if (!Renderer) return null;
          return <Renderer key={block.id} block={block} reveal={reveal} />;
        })}
        <BentoGrid />
      </div>
    </div>
  );
};

export default Home;
